// i18n
import { translateInComponent } from 'infras/i18n';
import translations from './i18n.json';

import styled from '@emotion/styled';
import {
  spacing,
  mediaMax,
  borderRadius,
  fontWeight,
  fontSize,
  isMediaMax,
  mediaMin,
} from 'presentations/views/styles';
import Progress from 'presentations/views/components/atoms/Progress';
import {
  Currency,
  getPlanName,
  getPriceText,
  IntervalType,
  PlanId,
} from 'domains/models/subscription/planId';
import {
  error,
  gray02,
  primary,
  white,
  whiteFix,
  gray01,
} from 'presentations/views/styles/color';
import Button from 'presentations/views/components/atoms/Button';
import Icon from 'presentations/views/components/atoms/Icon';
import { SyntheticEvent, useState } from 'react';
import { TrialChannel } from 'domains/models/subscription/trial_channel';
import Subscription from 'domains/models/subscription';
import { getDateTime } from 'infras/datetime';
import {
  latestPlanVersion,
  PlanVersion,
} from 'domains/models/subscription/planVersion';
import PricingTable from 'presentations/views/components/organisms/PricingTable';

interface Props {
  onClickPrice?: any;
  planId: PlanId | null;
  planVersion: PlanVersion | null;
  selectedPlanId?: PlanId | null;
  isLoading?: boolean;
  isLogin: boolean;
  canChange: boolean;
  trialStartedAt: string | null | undefined;
  trialEndedAt: string | null | undefined;
  trialChannel: TrialChannel | null | undefined;
  currency: Currency;
  intervalType: IntervalType;
  currentIntervalType: IntervalType | null;
  isChangeAnnualToMonth: boolean;
}
export default function Component(props: Props) {
  const {
    currency,
    planId,
    planVersion,
    intervalType,
    currentIntervalType,
  } = props;
  const t = translateInComponent(translations);
  const [isShowTablePlanIds, setIsShowTablePlanIds] = useState<PlanId[]>([]);
  const isCurrentEp = Boolean(planId && planId >= PlanId.Enterprice);
  const isMobile = isMediaMax('tablet');
  const plans = [
    {
      planId: PlanId.Free,
      title: t('Price name free'),
      price: t(`Price num free ${currency}`),
      period: t('Price period'),
      planDescription: t('Plan description free'),
    },
    {
      planId: PlanId.Pro,
      title: getPlanName(PlanId.Pro, latestPlanVersion),
      price: getPriceText(
        PlanId.Pro,
        currency,
        intervalType,
        latestPlanVersion,
      ),
      period: t('Price period'),
      planDescription: t('Plan description pro'),
    },
    {
      planId: PlanId.Team,
      title: getPlanName(PlanId.Team, latestPlanVersion),
      price: getPriceText(
        PlanId.Team,
        currency,
        intervalType,
        latestPlanVersion,
      ),
      period: t('Price period'),
      planDescription: t('Plan description team'),
    },
    {
      planId: PlanId.Enterprice,
      title: getPlanName(PlanId.Enterprice, planVersion),
      price: t('Price num enterprise'),
      period: t('Price period'),
      planDescription: t('Plan description enterprise'),
    },
  ];

  const onClickShowTable = (e: SyntheticEvent<HTMLDivElement>) => {
    const { planId } = e.currentTarget.dataset;
    const selectedPlanId = Number(planId);
    const planIds = [...isShowTablePlanIds];
    const index = isShowTablePlanIds.indexOf(selectedPlanId);
    if (index !== -1) {
      planIds.splice(index, 1);
    } else {
      planIds.push(selectedPlanId);
    }
    const newPlanIds = Array.from(new Set(planIds));
    setIsShowTablePlanIds(newPlanIds);
  };

  return (
    <Container>
      {plans.map((plan) => {
        const isJicooTrialing = Subscription.isJicooTrialing(
          props.trialChannel,
          getDateTime(props.trialEndedAt),
        );
        const isSelected = isJicooTrialing
          ? plan.planId === PlanId.Free
          : planId === plan.planId &&
            (plan.planId === PlanId.Free ||
              (planVersion === latestPlanVersion &&
                currentIntervalType === intervalType));
        let priceName = t('SignUp');
        const isEp = plan.planId === PlanId.Enterprice;
        if (props.isLogin) {
          if (isSelected) {
            priceName = t('Selected');
          } else {
            if (props.planId && props.planId < plan.planId) {
              priceName = props.trialStartedAt ? t('Upgrade') : t('Trial');
            } else {
              priceName = t('Select');
            }
          }
        } else {
          if (plan.planId >= PlanId.Pro) {
            priceName = t('Trial');
          }
        }
        if (isEp) {
          priceName = t('Contact');
        }
        const isDisabled = props.isLogin && props.canChange === false;
        const isRecommend = plan.planId === PlanId.Team;

        return (
          <ListItemContainer
            key={plan.planId}
            isRecommend={isRecommend}
            isEp={isEp}
            isDisabled={isDisabled}
          >
            {isRecommend && <RecommendTag>{t('Most Popular')}</RecommendTag>}
            <Plan>
              <PlanTitle>{plan.title}</PlanTitle>
              {isEp ? (
                <PlanEpDescription>{plan.planDescription}</PlanEpDescription>
              ) : (
                <PlanPrice>{plan.price}</PlanPrice>
              )}
              {t('Tax') && (
                <PlanPriceTax>{isEp ? <>&nbsp;</> : t('Tax')}</PlanPriceTax>
              )}
              {!isEp && <PlanPriceUnit>{plan.period}</PlanPriceUnit>}
              {!isEp && (
                <PlanDescription>{plan.planDescription}</PlanDescription>
              )}
              {isDisabled && <RoleWarning>{t('Role warn')}</RoleWarning>}
              <PlanButton
                isEp={isEp}
                variant={isEp ? 'outlined' : 'contained'}
                isDisabled={
                  isEp ||
                  isDisabled ||
                  props.isLoading ||
                  isSelected ||
                  props.isChangeAnnualToMonth ||
                  isCurrentEp
                }
                color="primary"
                onClick={(e: SyntheticEvent) => props.onClickPrice(plan.planId)}
                size="large"
              >
                {props.isLoading && props.selectedPlanId === plan.planId ? (
                  <Progress color="inherit" size={18} />
                ) : (
                  priceName
                )}
              </PlanButton>
            </Plan>
            {isShowTablePlanIds.indexOf(plan.planId) === -1 ? (
              <ShowAllFeatureLink
                data-plan-id={plan.planId}
                onClick={onClickShowTable}
              >
                <ExpandIcon name="expand_more" isEp={isEp} />
                <ExpandText>{t('Show all features')}</ExpandText>
              </ShowAllFeatureLink>
            ) : (
              <ShowAllFeatureLink
                data-plan-id={plan.planId}
                onClick={onClickShowTable}
              >
                <ExpandIcon name="expand_less" isEp={isEp} />
                <ExpandText>{t('Hide')}</ExpandText>
              </ShowAllFeatureLink>
            )}
            {isMobile && isShowTablePlanIds.indexOf(plan.planId) !== -1 && (
              <PricingTableContainer>
                <PricingTable
                  isLogin={props.isLogin}
                  planId={props.planId}
                  planVersion={props.planVersion}
                  canChange={props.canChange}
                  onClickPrice={props.onClickPrice}
                  isLoading={props.isLoading}
                  trialStartedAt={props.trialStartedAt}
                  trialEndedAt={props.trialEndedAt}
                  trialChannel={props.trialChannel}
                  currency={props.currency}
                  intervalType={props.intervalType}
                  currentIntervalType={props.currentIntervalType}
                  isChangeAnnualToMonth={props.isChangeAnnualToMonth}
                  filterPlanId={plan.planId}
                />
              </PricingTableContainer>
            )}
          </ListItemContainer>
        );
      })}
    </Container>
  );
}

const Container = styled.div`
  display: flex;
  margin: 0 auto;
  ${mediaMax.tablet} {
    flex-direction: column;
    justify-content: center;
    align-items: center;
    width: 100%;
  }
`;
const ListItemContainer = styled.div<{
  isRecommend?: boolean;
  isEp: boolean;
  isDisabled: boolean;
}>`
  width: 25%;
  margin-right: ${spacing(12)};
  background-color: ${white};
  border: ${(props) => props.isRecommend && `1px solid ${primary(props)}`};
  border-radius: ${borderRadius(16)};
  background-color: ${(props) => props.isEp && primary(props)};
  color: ${(props) => props.isEp && whiteFix};
  position: relative;
  padding: 0 ${spacing(12)};
  height: ${(props) => (props.isDisabled ? 480 : 400)}px;

  :last-of-type {
    margin-right: 0;
  }
  ${mediaMax.tablet} {
    height: initial;
    width: 100%;
    margin-right: 0;
    margin-bottom: ${spacing(24)};
  }
`;

const Plan = styled.div`
  padding: ${spacing(24)} ${spacing(12)} 0;
  text-align: center;
`;
const PlanTitle = styled.div`
  margin: ${spacing(24)} 0 ${spacing(16)};
  font-weight: ${fontWeight('bold')};
  font-size: ${fontSize(24)};
  ${mediaMax.tablet} {
    margin: ${spacing(24)} 0 ${spacing(2)};
  }
`;
const PlanPrice = styled.div`
  font-weight: ${fontWeight('bold')};
  font-size: ${fontSize(44)};
  line-height: 1;
  margin-bottom: ${spacing(4)};
  height: 48px;
  ${mediaMax.desktop} {
    font-size: ${fontSize(36)};
  }
`;
const PlanPriceTax = styled.div`
  margin-bottom: ${spacing(8)};
  color: ${gray02};
  font-size: ${fontSize(13)};
`;
const PlanPriceUnit = styled.div`
  margin-bottom: ${spacing(32)};
  color: ${gray02};
  font-size: ${fontSize(13)};
  ${mediaMax.tablet} {
    margin-bottom: ${spacing(16)};
  }
`;
const PlanDescription = styled.div`
  margin-bottom: ${spacing(32)};
  height: 48px;
  font-size: ${fontSize(13)};
  white-space: pre-wrap;
  ${mediaMax.desktop} {
    white-space: normal;
  }
  ${mediaMax.tablet} {
    white-space: pre-wrap;
    margin-bottom: ${spacing(24)};
    height: auto;
  }
`;
const PlanEpDescription = styled(PlanDescription)`
  margin-top: ${spacing(24)};
  ${mediaMax.tablet} {
    margin-bottom: 0;
  }
`;
const PlanButton = styled(Button)<{ isEp: boolean }>`
  position: absolute;
  bottom: 32px;
  right: 0;
  left: 0;
  margin-left: ${spacing(16)};
  width: calc(100% - 32px);
  background-color: ${(props) => props.isEp && primary};
  color: ${(props) => props.isEp && whiteFix};
  border-width: ${(props) => props.isEp && 1};
  border-color: ${(props) => props.isEp && whiteFix};
  ${mediaMax.desktop} {
    word-break: keep-all;
  }
  ${mediaMax.tablet} {
    margin-bottom: ${spacing(16)};
    position: static;
    width: 100%;
    margin-left: 0;
  }
`;
const RoleWarning = styled.div`
  color: ${error};
  font-size: ${fontSize(12)};
`;
const RecommendTag = styled.div`
  position: absolute;
  padding: ${spacing(8)} ${spacing(12)};
  max-width: 150px;
  max-height: 44px;
  background: ${white};
  box-shadow: 0px 0px 14px rgba(31, 116, 242, 0.15);
  display: flex;
  justify-content: center;
  align-items: center;
  text-transform: uppercase;
  position: absolute;
  margin: auto;
  border-radius: ${borderRadius(6)};
  width: fit-content;
  text-align: center;
  top: 0;
  left: 0;
  right: 0;
  transform: translateY(-50%);
  font-size: ${fontSize(12)};
  font-weight: ${fontWeight('bold')};
  color: ${primary};
`;

const PricingTableContainer = styled.div`
  margin-top: ${spacing(32)};
`;

const ShowAllFeatureLink = styled.div`
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  margin: ${spacing(16)} 0 ${spacing(24)};
  ${mediaMin.tablet} {
    display: none;
  }
`;
const ExpandIcon = styled(Icon)<{ isEp: boolean }>`
  width: 18px !important;
  height: 18px !important;
  color: ${(props) => (props.isEp ? whiteFix : primary)};
`;
const ExpandText = styled.div`
  margin-left: ${spacing(4)};
  /* color: ${primary}; */
  font-size: ${fontSize(16)};
  font-weight: ${fontWeight('bold')};
`;
